// Tells the models list that something else changed a model.
//
// The same shape as `signalsRefreshBus`, and for the same reason: the agent creates,
// renames, archives and duplicates through the ordinary routes, so the change lands
// exactly as a person's click would and the browser has no idea it happened. The write
// came from the server, not from a click, and the store's own reload never ran, so the
// row keeps the name it had until someone reloads the page.
//
// A bus rather than a prop chain because the emitter is the chat surface and the
// listener is the page around it, and neither is an ancestor of the other.
//
// Like signals and unlike trees, there is no target to carry: the list is one page per
// tenant, so there is nothing to choose between. A run of edits is still one reload.

/** How long to wait for more emits before reloading. */
const WINDOW_MS = 250;

export type ModelsRefreshHandler = () => void;

type Schedule = (run: () => void, delayMs: number) => void;

export function createModelsRefreshBus(
  schedule: Schedule = (run, delayMs) => {
    setTimeout(run, delayMs);
  },
  windowMs = WINDOW_MS
) {
  const handlers = new Set<ModelsRefreshHandler>();
  let scheduled = false;

  return {
    subscribe(handler: ModelsRefreshHandler) {
      handlers.add(handler);
      return () => {
        handlers.delete(handler);
      };
    },
    /**
     * Ask for a reload, coalesced.
     *
     * "Archive these four drafts" is four confirmed writes and four emits. Reloading
     * per emit would queue four requests behind each other and re-sort the table under
     * the reader three times on the way, so emits inside one window collapse.
     */
    emit() {
      if (scheduled) return;
      scheduled = true;
      schedule(() => {
        scheduled = false;
        handlers.forEach((handler) => handler());
      }, windowMs);
    },
  };
}

const bus = createModelsRefreshBus();

export const emitModelsRefresh = bus.emit;
export const onModelsRefresh = bus.subscribe;
